/**
 * Agent-context file scaffolding written by the CLI.
 *
 * One layer, one job: per-hub `wayai-ws/hubs/<hub>/AGENTS.md` — hub-specific
 * MEMORY, the context the config files (hub.yaml, agents/) can't capture. A
 * scaffold the agent fills in, seeded by `writeHubFolder` (yaml-writer.ts) via
 * `perHubAgentsMd`. No `CLAUDE.md` shim beside it: every supported harness reads
 * AGENTS.md natively (Claude Code from v2.1.277, in most setups), and where a
 * session doesn't, the WayAI skill has the agent read the file itself.
 *
 * The CLI does NOT seed a repo-root `AGENTS.md`/`CLAUDE.md`. Every supported
 * harness takes its instructions from the WayAI skill, which is the sole entry
 * point — a generic root bootstrap only restated what the skill already says and
 * went stale between releases.
 *
 * Every write is absence-guarded — a user's own file is NEVER overwritten.
 */

import * as path from 'node:path';
import { createFileNoFollow } from './fs-safety.js';

/**
 * Scaffold for a hub folder's `AGENTS.md` — hub-specific memory the agent fills
 * in. `hubFolderName` titles it (e.g. "support--abc123").
 */
export function perHubAgentsMd(hubFolderName: string): string {
  return [
    `# ${hubFolderName}`,
    '',
    "Notes about THIS hub that the config files (hub.yaml, agents/) don't capture:",
    'why it exists, key decisions and the reasons behind them, business rules that',
    'only apply here, terminology, integration quirks.',
    '',
    'Keep it short and about *why / what is different* — platform mechanics live in',
    'the WayAI skill. Fill this in as you learn about the hub; delete this',
    'placeholder once you do.',
    '',
  ].join('\n');
}

/**
 * Absence-guarded seed of `filePath` inside `root`, never following a symlink
 * (`createFileNoFollow`). Returns the basename if written; null if anything already
 * sits at the path — a dangling symlink included — or the path reaches `root`
 * only through a symlink.
 */
export function writeIfAbsent(root: string, filePath: string, content: string): string | null {
  return createFileNoFollow(root, filePath, Buffer.from(content, 'utf-8')) === 'created'
    ? path.basename(filePath)
    : null;
}
